/*
 * Mery – Kollektionsseite: Filter-Drawer (mobil), Filtern/Sortieren ohne Neuladen
 * (Shopify Section Rendering API), „Mehr laden“. Ohne JavaScript funktioniert alles per Formular/Links.
 */

class MeryCollection extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.panel = this.querySelector('[data-facets-panel]');
    this.backdrop = this.querySelector('.mery-facets__backdrop');
    this.abort = null;

    this.addEventListener('click', (event) => this.onClick(event));
    this.addEventListener('change', (event) => this.onChange(event));
    this.addEventListener('input', (event) => {
      if (event.target.matches('input[type="number"]')) this.onChange(event, 600);
    });
    this.addEventListener('submit', (event) => {
      if (!event.target.matches('[data-mery-facets-form]')) return;
      event.preventDefault();
      this.applyForm(event.target);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.panel?.hasAttribute('data-open')) this.closePanel();
    });
    window.addEventListener('popstate', () => this.render(window.location.href, { push: false }));
  }

  onClick(event) {
    const open = event.target.closest('[data-facets-open]');
    if (open) return this.openPanel(open);

    if (event.target.closest('[data-facets-close]')) return this.closePanel();

    const more = event.target.closest('[data-mery-load-more]');
    if (more) {
      event.preventDefault();
      return this.loadMore(more);
    }

    const link = event.target.closest('a[data-mery-facet-link]');
    if (link) {
      event.preventDefault();
      const isPagination = link.classList.contains('mery-pagination__link');
      this.render(link.href, { scroll: isPagination });
    }
  }

  onChange(event, delay = 250) {
    const form = event.target.closest('[data-mery-facets-form]');
    if (!form) return;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.applyForm(form), delay);
  }

  applyForm(form) {
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form)) {
      if (value !== '') params.append(key, value);
    }
    const url = `${form.action}?${params.toString()}`;
    this.render(url);
  }

  async fetchSection(url) {
    this.abort?.abort();
    this.abort = new AbortController();
    const fetchUrl = new URL(url, window.location.origin);
    fetchUrl.searchParams.set('section_id', this.sectionId);
    const response = await fetch(fetchUrl, { signal: this.abort.signal });
    const html = await response.text();
    return new DOMParser().parseFromString(html, 'text/html');
  }

  async render(url, { push = true, scroll = false } = {}) {
    this.setAttribute('aria-busy', 'true');
    try {
      const doc = await this.fetchSection(url);
      const next = doc.querySelector('mery-collection');
      if (!next) return;

      const openFacets = new Set(
        Array.from(this.querySelectorAll('details[data-facet][open]')).map((d) => d.dataset.facet)
      );
      const closedFacets = new Set(
        Array.from(this.querySelectorAll('details[data-facet]:not([open])')).map((d) => d.dataset.facet)
      );
      const focusedId = document.activeElement?.id;

      for (const selector of [
        '[data-mery-results]',
        '[data-mery-active]',
        '[data-mery-count]',
        '[data-mery-facets]',
        '.mery-collection__filter-toggle',
        '.mery-facets__footer',
      ]) {
        const current = this.querySelector(selector);
        const replacement = next.querySelector(selector);
        if (current && replacement) current.replaceWith(replacement);
      }

      this.querySelectorAll('details[data-facet]').forEach((details) => {
        if (openFacets.has(details.dataset.facet)) details.open = true;
        if (closedFacets.has(details.dataset.facet)) details.open = false;
      });
      if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });
      if (this.panel?.hasAttribute('data-open')) {
        this.querySelector('[data-facets-open]')?.setAttribute('aria-expanded', 'true');
      }

      if (push) history.pushState({}, '', url);
      if (scroll) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error) {
      if (error.name !== 'AbortError') window.location.href = url;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  async loadMore(button) {
    button.setAttribute('aria-busy', 'true');
    try {
      const doc = await this.fetchSection(button.href);
      const grid = this.querySelector('[data-mery-grid]');
      const newItems = doc.querySelectorAll('[data-mery-grid] > .mery-product-grid__item:not(.mery-product-grid__item--merch)');
      const firstNew = newItems[0];
      newItems.forEach((item) => grid.append(item));
      const currentMore = this.querySelector('[data-mery-more]');
      const nextMore = doc.querySelector('[data-mery-more]');
      if (nextMore) currentMore.replaceWith(nextMore);
      else currentMore.remove();
      firstNew?.querySelector('a[href]:not([tabindex="-1"])')?.focus({ preventScroll: true });
      window.MeryWishlist?.sync();
    } catch (error) {
      if (error.name !== 'AbortError') window.location.href = button.href;
    }
  }

  openPanel(trigger) {
    if (!this.panel) return;
    this.lastTrigger = trigger;
    this.panel.setAttribute('data-open', '');
    this.backdrop?.removeAttribute('hidden');
    trigger.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('mery-no-scroll');
    this.panel.querySelector('.mery-facets__close')?.focus();
  }

  closePanel() {
    if (!this.panel) return;
    this.panel.removeAttribute('data-open');
    this.backdrop?.setAttribute('hidden', '');
    document.documentElement.classList.remove('mery-no-scroll');
    const trigger = this.querySelector('[data-facets-open]');
    trigger?.setAttribute('aria-expanded', 'false');
    trigger?.focus();
  }
}

if (!customElements.get('mery-collection')) customElements.define('mery-collection', MeryCollection);
